import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useSyncedTransactions } from './SyncedTransactionsProvider'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    gap: '12px',
    padding: '8px 0',
    flexWrap: 'wrap'
  },
  containerCentered: {
    display: 'flex',
    gap: '20px',
    padding: '8px 0',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  containerFixed: {
    display: 'flex',
    gap: '20px',
    padding: '8px 0',
    flexWrap: 'wrap',
    justifyContent: 'flex-start'
  },
  cardBase: {
    borderRadius: 'var(--border-radius-md)',
    border: '1px solid var(--color-neutral-400)',
    padding: '14px 18px',
    cursor: 'pointer',
    userSelect: 'none',
    outline: '1px solid transparent',
    transition: 'border-color .2s ease-in-out, outline-color .2s ease-in-out, box-shadow .2s ease-in-out',
    '&:hover': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: theme.layout.shadow.normal
    },
    '&:active': {
      backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 10%, transparent)'
    },
    '&.__selected': {
      borderColor: 'var(--color-primary-500)',
      outline: '1px solid var(--color-primary-500)'
    }
  },
  card: {
    flex: '1 1 0',
    minWidth: '160px'
  },
  cardFixed: {
    flex: '0 0 auto',
    width: '340px'
  },
  title: {
    fontSize: 'var(--font-size-md)',
    fontWeight: 600,
    marginBottom: '8px',
    color: 'var(--text-color-default)'
  },
  metricsTable: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  metricLabel: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 300,
    color: 'var(--text-color-subtle)',
    paddingTop: '2px'
  },
  metricValue: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 700,
    textAlign: 'right',
    color: 'var(--text-color-default)',
    paddingTop: '2px'
  },
  loading: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--text-color-subtle)'
  }
}))

function formatMetricValue (formatter, value, format) {
  if (value == null) return '-'
  switch (format) {
    case 'currency':
      return formatter(value, 'marketValue')
    case 'percent':
      return formatter(value, 'percent')
    case 'number':
    default:
      return formatter(value, 'number')
  }
}

function MetricCard ({ group, values, isSelected, isLoading, onClick, cardClass }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()

  const handleClick = useCallback(() => {
    onClick(group.id)
  }, [onClick, group.id])

  return (
    <div
      role='button'
      tabIndex={0}
      aria-pressed={isSelected}
      className={clsx(classes.cardBase, cardClass, { __selected: isSelected })}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      <div className={classes.title}>{group.title}</div>
      {isLoading
        ? <div className={classes.loading}>Loading...</div>
        : (
          <table className={classes.metricsTable}>
            <tbody>
              {group.metrics.map((metric, i) => (
                <tr key={i}>
                  <td className={classes.metricLabel}>{metric.label}</td>
                  <td className={classes.metricValue}>
                    {formatMetricValue(formatter, values?.[i], metric.format)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  )
}

MetricCard.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    metrics: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      op: PropTypes.string,
      format: PropTypes.string
    }))
  }).isRequired,
  values: PropTypes.object,
  isSelected: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  cardClass: PropTypes.string
}

export default function SyncedTransactionsMetrics () {
  const classes = useStyles()
  const {
    config,
    activityData,
    isFetchingActivity,
    activeMetricGroupId,
    onMetricGroupToggle
  } = useSyncedTransactions()

  if (!config.showMetrics || !config.metricGroups?.length) return null

  const layout = config.metricLayout ?? 'stretch'
  const containerClass = layout === 'centered'
    ? classes.containerCentered
    : layout === 'fixed'
      ? classes.containerFixed
      : classes.container
  const cardClass = layout === 'stretch' ? classes.card : classes.cardFixed

  return (
    <div className={containerClass}>
      {config.metricGroups.map(group => (
        <MetricCard
          key={group.id}
          group={group}
          values={activityData?.[group.id]}
          isSelected={activeMetricGroupId === group.id}
          isLoading={isFetchingActivity && !activityData}
          onClick={onMetricGroupToggle}
          cardClass={cardClass}
        />
      ))}
    </div>
  )
}
